import { createContext, useState } from "react";
import { useDispatch } from "react-redux";

import {
  createProvinceService,
  deleteProvinceService,
  editProvinceService,
  getProvincesService,
  getCitiesService,
  createCityService,
  editCityService,
  deleteCityService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { isEmpty } from "lodash";
import { setCitiesAction } from "./../../../redux/actions/citiesActions/citiesActions";

export const provinceAndCityAdminContext = createContext({
  provinces: [],
  setProvinces: () => { },
  cities: [],
  setCities: () => { },
  id: "",
  setId: () => { },
  name: "",
  setName: () => { },
  province: "",
  setProvince: () => { },
  isEnabled: null,
  setIsEnabled: () => { },
  isShowModal: null,
  setIsShowModal: () => { },
  isShowSettingsModal: null,
  selectedCity: null,
  setSelectedCity: () => { },
  citySettingsList: [],
  setCitySettingsList: () => { },
  setIsShowSettingsModal: () => { },
  handleShowModal: () => { },
  handleCloseModal: () => { },
  handleGetProvinces: () => { },
  handleCreateProvince: () => { },
  handleEditProvince: () => { },
  handleDeleteProvince: () => { },
  handleGetCities: () => { },
  handleCreateCity: () => { },
  handleEditCity: () => { },
  handleDeleteCity: () => { },
  handleShowSettingsModal: () => { },
  handleCloseSettingsModal: () => { },
  handleAddSettingRow: () => { },
  handleRemoveSettingRow: () => { },
  handleSettingChange: () => { },
  handleSaveSettings: () => { },
  centerLat: 35.700954,
  setCenterLat: () => { },
  centerLng: 51.391173,
  setCenterLng: () => { },
  polygonPoints: [],
  setPolygonPoints: () => { },
});

const ProvinceAndCityAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [province, setProvince] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowSettingsModal, setIsShowSettingsModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [citySettingsList, setCitySettingsList] = useState([]);
  const [centerLat, setCenterLat] = useState(35.700954);
  const [centerLng, setCenterLng] = useState(51.391173);
  const [polygonPoints, setPolygonPoints] = useState([]);

  const handleResetState = () => {
    setId("");
    setName("");
    setProvince("");
    setIsEnabled(true);
    setCenterLat(35.700954);
    setCenterLng(51.391173);
    setPolygonPoints([]);
  };

  // Create Province And City Modal Operations
  const handleShowModal = (itemValue) => {
    setIsShowModal(true);
    if (itemValue) {
      setId(itemValue.id);
      setName(itemValue.name);
      setProvince(itemValue.province);
      setIsEnabled(itemValue.isEnabled);
      setCenterLat(itemValue.center_lat);
      setCenterLng(itemValue.center_lng);
      setPolygonPoints(itemValue.polygon);
    }
  };

  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  //* Start Province
  // Handle Get Provinces Method
  const handleGetProvinces = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getProvincesService();
      if (status === 200) {
        setProvinces(data.results);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Province Method
  const handleCreateProvince = async (event) => {
    event?.preventDefault();

    const objData = {
      name,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createProvinceService(objData);
      if (status === 201) {
        toastSuccessMessage("آیتم با موفقیت ایجاد شد");
        handleGetProvinces();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Province Method
  const handleEditProvince = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      name,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editProvinceService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetProvinces();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Province Method
  const handleDeleteProvince = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteProvinceService(id);
      if (status === 204) {
        toastSuccessMessage(`آیتم  ${id} با موفقیت حذف شد`);
        handleGetProvinces();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };
  //* End Province

  //* Start City
  // Handle Get Cities Method
  const handleGetCities = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCitiesService();
      if (status === 200) {
        setCities(data.results);

        dispatch(setCitiesAction(data.results));
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create City Method
  const handleCreateCity = async (event) => {
    event?.preventDefault();

    const objData = {
      name,
      isEnabled,
      province,
      center_lat: centerLat,
      center_lng: centerLng,
      polygon: polygonPoints
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createCityService(objData);
      if (status === 201) {
        toastSuccessMessage("آیتم با موفقیت ایجاد شد");
        handleGetCities();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit City Method
  const handleEditCity = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      name,
      isEnabled,
      province: parseInt(province),
      center_lat: centerLat,
      center_lng: centerLng,
      polygon: polygonPoints
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editCityService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetCities();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete City Method
  const handleDeleteCity = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteCityService(id);
      if (status === 204) {
        toastSuccessMessage(`آیتم  ${id} با موفقیت حذف شد`);
        handleGetCities();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  const handleShowSettingsModal = (city) => {
    setSelectedCity(city);
    const settingsObj = city.settings || {};
    const settingsArray = Object.keys(settingsObj).map((k) => ({
      key: k,
      value: settingsObj[k],
    }));
    setCitySettingsList(settingsArray);
    setIsShowSettingsModal(true);
  };

  const handleCloseSettingsModal = () => {
    setIsShowSettingsModal(false);
    setSelectedCity(null);
    setCitySettingsList([]);
  };

  const handleAddSettingRow = () => {
    setCitySettingsList([...citySettingsList, { key: "", value: "" }]);
  };

  const handleRemoveSettingRow = (index) => {
    const newList = [...citySettingsList];
    newList.splice(index, 1);
    setCitySettingsList(newList);
  };

  const handleSettingChange = (index, field, val) => {
    const newList = [...citySettingsList];
    newList[index][field] = val;
    setCitySettingsList(newList);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    const finalSettingsObj = {};
    citySettingsList.forEach((item) => {
      if (item.key.trim() !== "") {
        finalSettingsObj[item.key.trim()] = item.value;
      }
    });

    const objData = {
      id: selectedCity.id,
      name: selectedCity.name,
      isEnabled: selectedCity.isEnabled,
      province: parseInt(selectedCity.province),
      settings: finalSettingsObj
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editCityService(objData);
      if (status === 200) {
        toastSuccessMessage("تنظیمات با موفقیت ویرایش شد");
        handleGetCities();
        handleCloseSettingsModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  }
  //* End City

  return (
    <provinceAndCityAdminContext.Provider
      value={{
        provinces,
        setProvinces,
        cities,
        setCities,
        id,
        setId,
        name,
        setName,
        province,
        setProvince,
        isEnabled,
        setIsEnabled,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleCloseModal,
        handleGetProvinces,
        handleCreateProvince,
        handleEditProvince,
        handleDeleteProvince,
        handleGetCities,
        handleCreateCity,
        handleEditCity,
        handleDeleteCity,
        isShowSettingsModal,
        setIsShowSettingsModal,
        selectedCity,
        setSelectedCity,
        citySettingsList,
        setCitySettingsList,
        handleShowSettingsModal,
        handleCloseSettingsModal,
        handleAddSettingRow,
        handleRemoveSettingRow,
        handleSettingChange,
        handleSaveSettings,
        centerLat,
        setCenterLat,
        centerLng,
        setCenterLng,
        polygonPoints,
        setPolygonPoints,
      }}
    >
      {children}
    </provinceAndCityAdminContext.Provider>
  );
};

export default ProvinceAndCityAdminContext;
